import { useState } from 'react';
import { Icons } from '../Icons';
import { PINPad } from './PINPad';
import { pinService } from '../../services/pinService';
import './PINSetup.css';

interface PINSetupProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type SetupStep = 'create' | 'confirm' | 'success';

export function PINSetup({ isOpen, onClose, onSuccess }: PINSetupProps) {
  const [step, setStep] = useState<SetupStep>('create');
  const [firstPIN, setFirstPIN] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleFirstPIN = (pin: string) => {
    console.log('🔢 First PIN entered');
    setFirstPIN(pin);
    setError(null);
    setStep('confirm');
  };

  const handleConfirmPIN = async (pin: string) => {
    console.log('🔢 Confirm PIN entered');
    setLoading(true);
    setError(null);

    // Check if PINs match
    if (pin !== firstPIN) {
      console.log('❌ PINs do not match');
      setError('Les codes PIN ne correspondent pas');
      setLoading(false);
      // Reset to first step after error
      setTimeout(() => {
        setStep('create');
        setFirstPIN('');
        setError(null);
      }, 1500);
      return;
    }

    // PINs match, save to service
    try {
      // Create PIN with lockOnStart=false by default (can be enabled in Settings)
      await pinService.createPIN(pin, false);
      console.log('✅ PIN created successfully');

      setStep('success');

      // Close modal after success
      setTimeout(() => {
        if (onSuccess) onSuccess();
        onClose();
        // Reset state
        setStep('create');
        setFirstPIN('');
        setError(null);
      }, 2000);
    } catch (err: any) {
      console.error('❌ Error creating PIN:', err);
      setError(err.message || 'Erreur lors de la création du PIN');
      setLoading(false);
    }
  };

  const handleBack = () => {
    console.log('🔢 Back button pressed');
    setStep('create');
    setFirstPIN('');
    setError(null);
  };

  const handleCancel = () => {
    console.log('🔢 Cancel button pressed');
    onClose();
    // Reset state
    setTimeout(() => {
      setStep('create');
      setFirstPIN('');
      setError(null);
    }, 300);
  };

  return (
    <div className="biometric-modal-overlay" onClick={handleCancel}>
      <div className="pin-setup-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="pin-setup-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {step === 'confirm' && (
              <button className="back-btn" onClick={handleBack} aria-label="Back">
                <Icons.ChevronLeft size={24} />
              </button>
            )}
            <h2>
              {step === 'create' && 'Créer un code PIN'}
              {step === 'confirm' && 'Confirmer le code PIN'}
              {step === 'success' && 'PIN configuré !'}
            </h2>
          </div>
          <button className="close-btn" onClick={handleCancel} aria-label="Close">
            <Icons.X size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="pin-setup-body">
          {step === 'create' && (
            <>
              <PINPad
                length={6}
                onComplete={handleFirstPIN}
                title="Créez votre code PIN"
                subtitle="Choisissez un code de 4 à 6 chiffres"
                error={error}
              />
            </>
          )}

          {step === 'confirm' && (
            <PINPad
              length={6}
              onComplete={handleConfirmPIN}
              title="Confirmez votre code PIN"
              subtitle="Entrez à nouveau le même code"
              error={error}
              loading={loading}
            />
          )}

          {step === 'success' && (
            <div className="pin-success">
              <div className="success-icon">
                <Icons.CheckCircle size={64} color="#22c55e" />
              </div>
              <h3>Code PIN configuré !</h3>
              <p>
                Votre portfolio est maintenant protégé par votre code PIN.
              </p>
            </div>
          )}
        </div>

        {/* Footer hint */}
        {step !== 'success' && (
          <div className="pin-setup-footer">
            <div className="footer-hint">
              <Icons.Info size={16} style={{ color: 'var(--primary)', flexShrink: 0 }} />
              <span>
                Votre PIN est stocké de manière sécurisée (hash SHA-256). Ne le partagez avec personne.
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
